/**
 * Application Routes
 * Defines all routes for the application
 */

import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ProtectedRoute } from '../components/auth/ProtectedRoute';
import { RoleProtectedRoute } from '../components/auth/RoleProtectedRoute';
import DashboardPage from '../pages/dashboard/DashboardPage';
import MissionManagementPage from '../pages/missions/MissionManagementPage';
import MissionDetailPage from '../pages/missions/detail/MissionDetailPage';
import ReportsPage from '../pages/reports/ReportsPage';
import OperationsMonitorPage from '../pages/reports/OperationsMonitorPage';
import OverridesPage from '../pages/reports/OverridesPage';
import GhostPerformancePage from '../pages/reports/GhostPerformancePage';
import AssetBehaviorPage from '../pages/reports/AssetBehaviorPage';
import MarketplaceHealthPage from '../pages/reports/MarketplaceHealthPage';
import SystemHealthPage from '../pages/reports/SystemHealthPage';
import ComplianceRiskPage from '../pages/reports/ComplianceRiskPage';
import GhostVerificationPage from '../pages/ghostVerification/GhostVerificationPage';
import GhostVerificationDetailPage from '../pages/ghostVerification/detail/GhostVerificationDetailPage';
import ContentModerationPage from '../pages/contentModeration/ContentModerationPage';
import InvestigationDetailPage from '../pages/contentModeration/detail/InvestigationDetailPage';
import DisputesManagementPage from '../pages/disputes/DisputesManagementPage';
import DisputeDetailPage from '../pages/disputes/detail/DisputeDetailPage';
import ComplaintsManagementPage from '../pages/complaints/ComplaintsManagementPage';
import ComplaintDetailPage from '../pages/complaints/detail/ComplaintDetailPage';
import UserManagementPage from '../pages/users/UserManagementPage';
import UserDetailPage from '../pages/users/detail/UserDetailPage';
import AssetDetailPage from '../pages/users/detail/AssetDetailPage';
import AdminUserManagementPage from '../pages/settings/AdminUserManagementPage';
import AddAdminUserPage from '../pages/settings/AddAdminUserPage';
import EditAdminUserPage from '../pages/settings/EditAdminUserPage';
import ChangePasswordPage from '../pages/settings/ChangePasswordPage';
import FinancePage from '../pages/finance/FinancePage';
import FinanceDetailPage from '../pages/finance/detail/FinanceDetailPage';
import TrainingPage from '../pages/training/TrainingPage';
import AuditLogPage from '../pages/reports/AuditLogPage';
import LoginPage from '../pages/auth/LoginPage';
import AccountDeletionPage from '../pages/public/AccountDeletionPage';
import AccountDeletionSuccessPage from '../pages/public/AccountDeletionSuccessPage';
import { AuthService } from '../services/auth/AuthService';

// Public route component - redirects to dashboard if already logged in
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuthenticated = AuthService.isAuthenticated();
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
};

export const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />

      {/* Public account deletion page - no login required, no admin layout */}
      <Route
        path="/account/delete"
        element={<AccountDeletionPage />}
      />
      <Route
        path="/account/delete/success"
        element={<AccountDeletionSuccessPage />}
      />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/missions"
        element={
          <ProtectedRoute>
            <MissionManagementPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/missions/:id"
        element={
          <ProtectedRoute>
            <MissionDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/content-moderation"
        element={
          <ProtectedRoute>
            <ContentModerationPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/content-moderation/:id"
        element={
          <ProtectedRoute>
            <InvestigationDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/disputes"
        element={
          <ProtectedRoute>
            <DisputesManagementPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/disputes/:id"
        element={
          <ProtectedRoute>
            <DisputeDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/complaints"
        element={
          <ProtectedRoute>
            <ComplaintsManagementPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/complaints/:id"
        element={
          <ProtectedRoute>
            <ComplaintDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute>
            <UserManagementPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/users/:id"
        element={
          <ProtectedRoute>
            <UserDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/users/asset/:id"
        element={
          <ProtectedRoute>
            <AssetDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ghost-verification"
        element={
          <ProtectedRoute>
            <GhostVerificationPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ghost-verification/:id"
        element={
          <ProtectedRoute>
            <GhostVerificationDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <ReportsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/operations-monitor"
        element={
          <ProtectedRoute>
            <OperationsMonitorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/overrides"
        element={
          <ProtectedRoute>
            <OverridesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/ghost-performance"
        element={
          <ProtectedRoute>
            <GhostPerformancePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/asset-behavior"
        element={
          <ProtectedRoute>
            <AssetBehaviorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/marketplace-health"
        element={
          <ProtectedRoute>
            <MarketplaceHealthPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/system-health"
        element={
          <ProtectedRoute>
            <SystemHealthPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/compliance-risk"
        element={
          <ProtectedRoute>
            <ComplianceRiskPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/audit-log"
        element={
          <RoleProtectedRoute requireAdminUserManagement>
            <AuditLogPage />
          </RoleProtectedRoute>
        }
      />
      {/* Change Password - available to all authenticated users */}
      <Route
        path="/settings/change-password"
        element={
          <ProtectedRoute>
            <ChangePasswordPage />
          </ProtectedRoute>
        }
      />
      {/* Settings base route - redirect to change password */}
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Navigate to="/settings/change-password" replace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/admin-users"
        element={
          <RoleProtectedRoute requireAdminUserManagement>
            <AdminUserManagementPage />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/settings/admin-users/add"
        element={
          <RoleProtectedRoute requireAdminUserManagement>
            <AddAdminUserPage />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/settings/admin-users/edit/:id"
        element={
          <RoleProtectedRoute requireAdminUserManagement>
            <EditAdminUserPage />
          </RoleProtectedRoute>
        }
      />
      {/* Catch all Settings routes - redirect to dashboard */}
      <Route
        path="/settings/*"
        element={<Navigate to="/" replace />}
      />
      <Route
        path="/finance"
        element={
          <ProtectedRoute>
            <FinancePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/finance/:id"
        element={
          <ProtectedRoute>
            <FinanceDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/training"
        element={
          <ProtectedRoute>
            <TrainingPage />
          </ProtectedRoute>
        }
      />

      {/* Catch all - redirect to dashboard */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

